import { useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

type RouteSeo = {
  title: string;
  description: string;
  indexable: boolean;
};

const DEFAULT_SEO: RouteSeo = {
  title: 'ScoreNest | Property Decision Reports',
  description:
    'ScoreNest helps buyers, renters, investors, and agents compare Michigan property addresses using clear local signals.',
  indexable: true,
};
const PUBLIC_SITE_ORIGIN = 'https://scorenest.com';
const DEFAULT_OG_IMAGE = 'https://scorenest.com/images/logo/Logo-coffee-short.png';

const ROUTE_SEO: Record<string, RouteSeo> = {
  '/': {
    title: 'ScoreNest | Property Decision Reports',
    description:
      'Check a Michigan property address before you buy, rent, invest, bid, or recommend it to a client.',
    indexable: true,
  },
  '/methodology': {
    title: 'ScoreNest Methodology | How Scores Are Calculated',
    description:
      'See how ScoreNest builds each score from clear data and simple rules.',
    indexable: true,
  },
  '/pricing': {
    title: 'ScoreNest Pricing | Self-Service Report Credits',
    description:
      'Buy self-service ScoreNest report credits for Michigan property address checks.',
    indexable: true,
  },
  '/privacy-policy': {
    title: 'Privacy Policy | ScoreNest',
    description: 'Read how ScoreNest handles your data, account privacy, and report-related information.',
    indexable: true,
  },
  '/terms-of-service': {
    title: 'Terms of Service | ScoreNest',
    description: 'Review ScoreNest terms, acceptable use, and legal conditions for the platform.',
    indexable: true,
  },
  '/cookie-policy': {
    title: 'Cookie Policy | ScoreNest',
    description: 'Understand how ScoreNest uses cookies for security, attribution, and product analytics.',
    indexable: true,
  },
  '/refund-policy': {
    title: 'Refund Policy | ScoreNest',
    description: 'Review ScoreNest refund eligibility, windows, and processing rules for report package purchases.',
    indexable: true,
  },
};

const ROUTE_PATTERNS: Array<{ re: RegExp; seo: RouteSeo }> = [
  {
    re: /^\/report\/[^/]+\/conclusion$/,
    seo: {
      title: 'Property Snapshot | ScoreNest',
      description: 'Private ScoreNest property snapshot.',
      indexable: false,
    },
  },
  {
    re: /^\/report\/[^/]+(?:\/save-money)?$/,
    seo: {
      title: 'Property Report | ScoreNest',
      description: 'Private ScoreNest property report.',
      indexable: false,
    },
  },
  {
    re: /^\/reports(?:\/|$)/,
    seo: {
      title: 'My Reports | ScoreNest',
      description: 'Private report history and access links.',
      indexable: false,
    },
  },
  {
    re: /^\/shared\/[^/]+$/,
    seo: {
      title: 'Shared Report | ScoreNest',
      description: 'Private shared report route.',
      indexable: false,
    },
  },
  {
    re: /^\/compare$/,
    seo: {
      title: 'Compare Reports | ScoreNest',
      description: 'Private report comparison page.',
      indexable: false,
    },
  },
  {
    re: /^\/admin(?:\/|$)/,
    seo: {
      title: 'Admin | ScoreNest',
      description: 'Internal ScoreNest admin panel.',
      indexable: false,
    },
  },
  {
    re: /^\/admin-panel(?:\/|$)/,
    seo: {
      title: 'Admin | ScoreNest',
      description: 'Internal ScoreNest admin panel.',
      indexable: false,
    },
  },
];

function normalizePath(pathname: string): string {
  if (!pathname) return '/';
  const clean = pathname.replace(/\/+$/, '');
  return clean || '/';
}

function resolveRouteSeo(pathname: string): RouteSeo {
  const normalized = normalizePath(pathname);
  if (ROUTE_SEO[normalized]) {
    return ROUTE_SEO[normalized];
  }
  const matched = ROUTE_PATTERNS.find(item => item.re.test(normalized));
  if (matched) {
    return matched.seo;
  }
  return {
    ...DEFAULT_SEO,
    indexable: false,
    title: 'Page Not Found | ScoreNest',
    description: 'This page is unavailable or private.',
  };
}

function upsertMeta(name: string, content: string): void {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function upsertPropertyMeta(property: string, content: string): void {
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

export function RouteRobotsMeta() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathname = normalizePath(location.pathname);
    if (pathname !== '/' || !location.search.includes('t=')) return;

    const params = new URLSearchParams(location.search);
    if (!params.has('t')) return;

    params.delete('t');
    const search = params.toString();
    navigate(
      {
        pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    const pathname = normalizePath(location.pathname);
    const seo = resolveRouteSeo(pathname);
    const robots = seo.indexable
      ? 'index, follow, max-image-preview:large'
      : 'noindex, nofollow, noarchive, nosnippet';
    const canonical = `${PUBLIC_SITE_ORIGIN}${pathname}`;

    document.title = seo.title;
    upsertMeta('description', seo.description);
    upsertMeta('robots', robots);
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', seo.title);
    upsertMeta('twitter:description', seo.description);

    upsertPropertyMeta('og:type', 'website');
    upsertPropertyMeta('og:title', seo.title);
    upsertPropertyMeta('og:description', seo.description);
    upsertPropertyMeta('og:url', canonical);
    upsertPropertyMeta('og:image', DEFAULT_OG_IMAGE);
    upsertMeta('twitter:image', DEFAULT_OG_IMAGE);

    upsertCanonical(canonical);
  }, [location.pathname]);

  return null;
}
